/* eslint-disable no-underscore-dangle */
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  input,
  output,
  signal
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators
} from '@angular/forms';
import {
  Subject,
  takeUntil
} from 'rxjs';

import { CommonModule } from '@angular/common';
import { COMMON } from '@sondermind/constants';
import { PartialProviderWithId, ProviderUserService } from '@sondermind/data-access/provider-user';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { ErrorListComponent } from '@sondermind/features/error-list';
import { AlertModalComponent } from '@sondermind/features/modals/alert';
import { PrimaryVirtualAddressCardComponent } from '@sondermind/features/provider-facing/account';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import { GoogleMapsAutoCompleteDirective } from '@sondermind/google-maps';
import { IrisButtonContainerAlignments, IrisButtonModule } from '@sondermind/iris/button';
import { IrisCardModule } from '@sondermind/iris/card';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { IrisDropdownModule } from '@sondermind/iris/dropdown';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisTextInputModule } from '@sondermind/iris/text-input';

import { ServerResponse } from '@sondermind/utilities/models-http';
import { Address } from '@sondermind/utilities/models-location';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { FieldMaskingService } from '@sondermind/utilities/services/field-masking.service';
import {
  PasswordValidator,
  ZipcodeValidator
} from '@sondermind/utilities/tools';
import { FieldMatchValidator } from '@sondermind/utilities/tools/validators/field-match.validator';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';
import { StateCodeValidator } from '@sondermind/utilities/tools/validators/state-code.validator';

import { setAddressObject } from '@sondermind/core/utilities/autocomplete-address-selector';
/**
 * This displays a provider's account information. While it is in the "common" module,
 * it is not common across clients and providers and should only be used for providers.
 */
@Component({
  selector: 'provider-account-info-card',
  templateUrl: './account-info-card.component.html',
  providers: [ProviderUserService],
  styleUrls: ['./account-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IrisButtonModule,
    IrisCheckboxModule,
    IrisFormFieldModule,
    IrisTextInputModule,
    IrisDropdownModule,
    IrisCardModule,
    GoogleMapsAutoCompleteDirective,
    AlertModalComponent,
    PrimaryVirtualAddressCardComponent,
  ]
})
export class AccountInfoCardComponent implements OnInit, OnDestroy {
  readonly user = input<ProviderUser>(new ProviderUser());
  readonly primaryVirtualAddress = input<Address>(null);
  readonly hidePassword = input<boolean>(false);
  readonly hideMailingAddress = input<boolean>(true);
  readonly showVirtualAddress = input<boolean>(false);
  modelSaved = output<ProviderUser>();

  readonly saving = signal<boolean>(false);
  readonly primaryVirtualAddressNotEmpty = signal<boolean>(false);
  readonly isSaasEnabled = signal<boolean>(false);

  @ViewChild('alertMessage') alertModal: AlertModalComponent;
  @ViewChild('errorList') errorList: ErrorListComponent;

  private destroy$: Subject<void> = new Subject<void>();

  form: UntypedFormGroup = null;

  addressErrors = {
    zipcodeFormat: 'Zipcode must be numeric and no more than 9 digits',
    stateCode: 'Must be valid uppercase state abbreviation',
  };

  readonly supportEmail = computed(() => (this.isSaasEnabled() ? COMMON.SAAS_SUPPORT_EMAIL : COMMON.SUPPORT_EMAIL));
  legalEmailLink: string = `${this.supportEmail()}?subject=Legal and tax information change request`;
  readonly addressMissingMessage = computed(() => (this.isSaasEnabled()
    ? 'Contact support to add your legal address.'
    : 'Contact your Provider Coach to add your legal address.')
  );
  irisButtonContainerAlignments = IrisButtonContainerAlignments;

  updateUserControl = effect(() => {
    const currentUser = this.user();
    if (currentUser?.loaded) {
      this.updateControls();
    }
  });

  addressEmpty = effect(() => {
    const virtualAddress = this.primaryVirtualAddress();
    if (virtualAddress) {
      this.primaryVirtualAddressNotEmpty.set(this.addressNotEmpty(virtualAddress));
    }
  });

  checkMailingAddress = effect(() => {
    const currentUser = this.user();
    if (currentUser && this.mailingAddressIsVisible) {
      this.checkForMailingAddress();
    }
  });

  constructor(
    private _appService: SondermindApplicationService,
    private _userService: ProviderUserService,
    private _builder: UntypedFormBuilder,
    public _maskingService: FieldMaskingService,
    private notificationToastService: NotificationToastService,
  ) {
    const passwordFields = this.hidePassword() ? {
      password: new UntypedFormControl(null),
      passwordConfirmation: new UntypedFormControl(null),
    } : {
      password: new UntypedFormControl(null, Validators.compose([PasswordValidator.format])),
      passwordConfirmation: new UntypedFormControl(null, Validators.compose([PasswordValidator.format]))
    };

    this.form = this._builder.group({
      firstName: new FormControl({ value: null, disabled: true }),
      middleName: new UntypedFormControl(null),
      lastName: new UntypedFormControl(null),
      displayName: new FormControl({ value: null, disabled: true }),
      email: new FormControl({ value: null, disabled: true }),
      mailingAddress: this._builder.group({
        addressLine1: new FormControl(null),
        addressLine2: new FormControl(null),
        city: new FormControl(null),
        state: new FormControl(null, [StateCodeValidator.format]),
        postalCode: new FormControl(null, [ZipcodeValidator.format])
      }),
      phoneNumber: new UntypedFormControl(null, Validators.compose([Validators.required, PhoneValidator.format])),
      timeZone: new FormControl({ value: null, disabled: true }),
      practiceName: new UntypedFormControl(null),
      subscriptionSms: new UntypedFormControl(null),
      mailingAddressIsDifferent: new UntypedFormControl(false),
      cellphoneNumber: new UntypedFormControl(null, Validators.compose([PhoneValidator.format])),
      ...passwordFields
    }, {
      validator: FieldMatchValidator.compareFields('password', 'passwordConfirmation', 'Passwords do not match')
    });
  }

  ngOnInit(): void {
    this.isSaasEnabled.set(this._appService.isSaasProvider());

    this._appService.pageLoaded = true;

    this.initializeCellPhoneControlSubscription();

    if (this.hideMailingAddress()) {
      const mailingAddressFormGroup = this.form.get('mailingAddress');
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const key in mailingAddressFormGroup.value) {
        mailingAddressFormGroup.get(key).clearValidators();
        mailingAddressFormGroup.get(key).updateValueAndValidity();
      }

      this.form.setValue({
        mailingAddress: null
      });
    }
  }

  initializeCellPhoneControlSubscription(): void {
    const cellControl = this.form.get('cellphoneNumber');
    const smsControl = this.form.get('subscriptionSms');

    if (!cellControl.value) {
      smsControl.disable();
    }

    cellControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      if (!value || cellControl.invalid) {
        smsControl.disable();
      } else {
        smsControl.enable();
      }
    });
  }

  checkForMailingAddress(): void {
    const currentUser = this.user();
    if (!currentUser.mailingAddress.addressLine1 && currentUser.billingAddress.addressLine1) {
      const updatedUser: PartialProviderWithId = { ...currentUser };

      updatedUser.mailingAddress.addressLine1 = currentUser.billingAddress.addressLine1;
      updatedUser.mailingAddress.addressLine2 = currentUser.billingAddress.addressLine2;
      updatedUser.mailingAddress.city = currentUser.billingAddress.city;
      updatedUser.mailingAddress.state = currentUser.billingAddress.state;
      updatedUser.mailingAddress.postalCode = currentUser.billingAddress.postalCode;
      this.updateUser(updatedUser, true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCancel(): void {
    this.updateControls();
  }

  onSubmit(): void {
    if (this.saving() || !this.form.valid) {
      if (!this.form.get('mailingAddress').valid) {
        this.notificationToastService.showNotificationToast({
          message: 'Error saving. Mailing address is required',
          showCloseButton: true,
          duration: 30000,
          type: ToastType.ERROR,
        });
      } else {
        this.notificationToastService.showNotificationToast({
          message: 'Error saving. Please fill all required fields.',
          showCloseButton: true,
          duration: 30000,
          type: ToastType.ERROR,
        });
      }
      Object.keys(this.form.controls).forEach((field) => {
        const control = this.form.get(field);
        control.markAsTouched({ onlySelf: true });
      });
      return;
    }

    this.saving.set(true);

    const data = this.form.value;
    const updateUser: ProviderUser = new ProviderUser();

    updateUser.id = this.user().id;
    updateUser.firstName = data.firstName;
    updateUser.middleName = data.middleName;
    updateUser.lastName = data.lastName;
    updateUser.subscriptionSms = data.subscriptionSms;
    updateUser.practiceName = data.practiceName;
    updateUser.timeZone = data.timeZone;

    updateUser.phoneNumber = data.phoneNumber;
    updateUser.cellphoneNumber = data.cellphoneNumber;

    if (!this.hidePassword()) {
      updateUser.password = data.password;
      updateUser.passwordConfirmation = data.passwordConfirmation;
    }

    if (this.mailingAddressIsVisible) {
      if (!this.form.get('mailingAddressIsDifferent').value) {
        updateUser.mailingAddress = this.user().billingAddress;
      } else {
        updateUser.mailingAddress = data.mailingAddress;
      }
    }

    if (!data.cellphoneNumber) {
      updateUser.subscriptionSms = null;
    }

    this.updateUser(updateUser);
  }

  updateUser(user: PartialProviderWithId, noMailingAddress = false): void {
    this._userService.update(user).subscribe({
      next: (resp) => {
        this.saving.set(false);
        this.saveSuccess(resp);
        if (!noMailingAddress) {
          this.notificationToastService.showNotificationToast({
            message: 'Profile saved successfully.',
            showCloseButton: true,
            duration: 2000,
            type: ToastType.SUCCESS,
          });
        }
      },
      error: (err) => {
        this.saveError(err);
        this.saving.set(false);
        if (!noMailingAddress) {
          this.notificationToastService.showNotificationToast({
            message: 'Error: Profile NOT saved. Please try again.',
            showCloseButton: true,
            duration: 2000,
            type: ToastType.ERROR,
          });
        }
      }
    });
  }

  get mailingAddressIsVisible(): boolean {
    return !this.hideMailingAddress();
  }

  addressNotEmpty(address: Address): boolean {
    return address.addressLine1?.length > 0
    || address.city?.length > 0
    || address.state?.length > 0
    || address.postalCode?.length > 0;
  }

  private updateControls(): void {
    const currentUser = this.user();
    this.form.setValue({
      firstName: currentUser.firstName,
      middleName: currentUser.middleName,
      lastName: currentUser.lastName,
      displayName: currentUser.displayName,
      email: currentUser.email,
      password: null,
      passwordConfirmation: null,
      phoneNumber: currentUser.phoneNumber,
      timeZone: currentUser.timeZone,
      cellphoneNumber: currentUser.cellphoneNumber,
      subscriptionSms: currentUser.subscriptionSms,
      practiceName: currentUser.practiceName,
      mailingAddress: {
        addressLine1: currentUser.mailingAddress.addressLine1,
        addressLine2: currentUser.mailingAddress.addressLine2,
        city: currentUser.mailingAddress.city,
        state: currentUser.mailingAddress.state,
        postalCode: currentUser.mailingAddress.postalCode
      },
      mailingAddressIsDifferent: false
    });

    if (
      this.mailingAddressIsVisible
      && currentUser.mailingAddress.addressLine1
      && currentUser.mailingAddress.addressLine1 !== currentUser.billingAddress.addressLine1
    ) {
      this.form.get('mailingAddressIsDifferent').setValue(true);
    }
  }

  private saveSuccess(serverResponse: ServerResponse): void {
    this._appService.publishErrors(serverResponse);

    if (serverResponse.has_value()) {
      this._appService.reloadCurrentUser();
      this.updateControls();
      this.modelSaved.emit(serverResponse.value);
    }
  }

  private saveError(serverResponse: any): void {
    this._appService.publishErrors(serverResponse);
  }

  toggleMailingAddress(): void {
    const mailingAddress = this.form.get('mailingAddress');
    const fields = ['addressLine1', 'city', 'state', 'postalCode'];

    if (this.form.get('mailingAddressIsDifferent').value) {
      fields.forEach((field) => {
        const control = mailingAddress.get(field);
        control.reset();
        // Set validators, including specific ones for state and postal code
        if (field === 'state') {
          control.setValidators([Validators.required, StateCodeValidator.format]);
        } else if (field === 'postalCode') {
          control.setValidators([Validators.required, ZipcodeValidator.format]);
        } else {
          control.setValidators([Validators.required]);
        }
        control.updateValueAndValidity();
      });
    } else {
      const currentUser = this.user();
      fields.forEach((field) => {
        const control = mailingAddress.get(field);
        control.clearValidators();
        control.setValue(currentUser.billingAddress[field]);
        control.updateValueAndValidity();
      });
    }
  }

  locationSelected(e): void {
    setAddressObject(e.address_components,
      this.form,
      {
        city: 'mailingAddress.city',
        state: 'mailingAddress.state',
        addressLine1: 'mailingAddress.addressLine1',
        addressLine2: 'mailingAddress.addressLine2',
        postalCode: 'mailingAddress.postalCode'
      });
  }
}
